import { Col, Flex, Row, Skeleton } from 'antd';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

// components
import Riyal from 'components/riyal/riyal';
import StatisticsItemComponent from './statisticsItem';
import NumberElement from 'components/number/number';

// models
import { DashboardStatistics } from '../models/AccountStatus';
import PriceElement from 'components/price/price';
import useCooperative from 'core/hooks/useCooperative';
import { useSelector } from 'react-redux';
import { RootState } from 'stores/store';

interface StatisticsComponentProps {
	isLoading: boolean;
	statistics: DashboardStatistics | null;
}

const StatisticsComponent: FunctionComponent<StatisticsComponentProps> = ({ isLoading, statistics }) => {
	const { t } = useTranslation();
	const { isCooperative } = useCooperative();
	const company = useSelector((state: RootState) => state.company);

	// cooperative societies have a fixed share value, so only their basic figures are shown
	if (isCooperative) {
		const cooperativeItems = [
			{ key: 'capital', lg: 8, headerTitle: t('DASHBOARD.CAPITAL'), bodyTitle: <PriceElement value={company?.capital || 0} riyalSize={23} /> },
			{
				key: 'shares',
				lg: 8,
				headerTitle: t('DASHBOARD.NUMBER_OF_SHARES'),
				bodyTitle: <NumberElement value={statistics?.company_shares.total_shares} />,
			},
			{
				key: 'members',
				lg: 8,
				headerTitle: t('DASHBOARD.SHAREHOLDERS'),
				bodyTitle: <NumberElement value={statistics?.shareholders.current_count} />,
			},
			{
				key: 'transactions',
				lg: 12,
				headerTitle: t('DASHBOARD.TRANSACTIONS'),
				bodyTitle: <NumberElement value={statistics?.transactions.today_count} />,
			},
			{
				key: 'annual-traffic',
				lg: 12,
				headerDesc: `${t('DASHBOARD.YEAR')} ${new Date().getFullYear()}`,
				headerTitle: t('DASHBOARD.ANNUAL_TRAFFIC'),
				bodyTitle: <NumberElement value={statistics?.annual_traffic.current_year} />,
				
			},
		];

		return (
			<Row gutter={[16, 16]}>
				{cooperativeItems.map(({ key, lg, ...item }) => (
					<Col key={key} span={24} sm={12} lg={lg}>
						{isLoading ? <Skeleton.Button active block style={{ minHeight: 135 }} /> : <StatisticsItemComponent {...item} />}
					</Col>
				))}
			</Row>
		);
	}

	return (
		<Row gutter={[16, 16]}>
			<Col span={24} lg={12}>
				{isLoading ? (
					<Skeleton.Button active block style={{ minHeight: 135 }} />
				) : (
					<StatisticsItemComponent
						headerIcon
						headerDesc={t('DASHBOARD.DAILY_CHANGE')}
						bodyDesc={t('DASHBOARD.AVERAGE_SALE_PRICE')}
						bodyTitle={<PriceElement value={statistics?.share_price.current || 0} riyalSize={23} decimalScale={2} />}
						change={statistics?.share_price.daily_change_ratio}
					/>
				)}
			</Col>

			<Col span={24} sm={12} md={8} lg={4}>
				{isLoading ? (
					<Skeleton.Button active block style={{ minHeight: 135 }} />
				) : (
					<StatisticsItemComponent
						headerIcon
						headerDesc={t('DASHBOARD.DAILY_CHANGE')}
						bodyDesc={t('DASHBOARD.SHAREHOLDERS')}
						bodyTitle={<NumberElement value={statistics?.shareholders.current_count} />}
						change={statistics?.shareholders.daily_change_ratio}
					/>
				)}
			</Col>

			<Col span={24} sm={12} md={8} lg={4}>
				{isLoading ? (
					<Skeleton.Button active block style={{ minHeight: 135 }} />
				) : (
					<StatisticsItemComponent
						headerIcon
						headerDesc={t('DASHBOARD.DAILY_CHANGE')}
						bodyDesc={t('DASHBOARD.TRANSACTIONS')}
						bodyTitle={<NumberElement value={statistics?.transactions.today_count} />}
						change={statistics?.transactions.daily_change_ratio}
					/>
				)}
			</Col>

			<Col span={24} sm={12} md={8} lg={4}>
				{isLoading ? (
					<Skeleton.Button active block style={{ minHeight: 135 }} />
				) : (
					<StatisticsItemComponent
						headerIcon
						bodyDesc={t('DASHBOARD.LAST_SHARE_PRICE')}
						bodyTitle={<PriceElement value={statistics?.share_price.last_price || 0} riyalSize={23} decimalScale={2} />}
						change={statistics?.share_price.daily_change_ratio}
					/>
				)}
			</Col>

			<Col span={24} sm={12} md={8} lg={4}>
				{isLoading ? (
					<Skeleton.Button active block style={{ minHeight: 135 }} />
				) : (
					<StatisticsItemComponent
						headerTitle={t('DASHBOARD.SHAREHOLDERS_SHARES')}
						bodyTitle={<NumberElement value={statistics?.company_shares.shareholders_shares} />}
					/>
				)}
			</Col>

			<Col span={24} sm={12} md={8} lg={4}>
				{isLoading ? (
					<Skeleton.Button active block style={{ minHeight: 135 }} />
				) : (
					<StatisticsItemComponent
						headerTitle={t('COMMON.PREFERRED_SHARES')}
						bodyTitle={<NumberElement value={statistics?.company_shares.preferred_shares} />}
					/>
				)}
			</Col>

			<Col span={24} sm={12} md={8} lg={4}>
				{isLoading ? (
					<Skeleton.Button active block style={{ minHeight: 135 }} />
				) : (
					<StatisticsItemComponent
						headerTitle={t('DASHBOARD.TREASURY_SHARES')}
						bodyTitle={<NumberElement value={statistics?.company_shares.number_of_treasury_shares} />}
					/>
				)}
			</Col>

			<Col span={24} sm={12} lg={4}>
				{isLoading ? (
					<Skeleton.Button active block style={{ minHeight: 135 }} />
				) : (
					<StatisticsItemComponent
						headerTitle={t('DASHBOARD.TOTAL_CAPITAL_SHARES')}
						bodyTitle={<NumberElement value={statistics?.company_shares.total_shares} />}
					/>
				)}
			</Col>

			<Col span={24} sm={12} lg={8}>
				{isLoading ? (
					<Skeleton.Button active block style={{ minHeight: 135 }} />
				) : (
					<StatisticsItemComponent
						headerIcon
						headerDesc={`${t('DASHBOARD.YEAR')} ${new Date().getFullYear()}`}
						bodyDesc={t('DASHBOARD.ANNUAL_TRAFFIC')}
						bodyTitle={<NumberElement value={statistics?.annual_traffic.current_year} />}
						change={statistics?.annual_traffic.annual_change_ratio}
					/>
				)}
			</Col>
		</Row>
	);
};

export default StatisticsComponent;
